import { Box, Button, Radio } from '@chakra-ui/react';
import { Ellipsis } from 'library/atoms/Elipsis';
import { useEffect } from 'react';
import { generatePath } from 'react-router-dom';
import Grid from 'uie/components/Grid';
import { generateDonutDataForNoiseReduction } from 'views/main/organization/analytics-v3/helpers';
import { PaginationType, SortBy } from 'views/main/organization/analytics-v3/hooks/types';
import { useAnalyticsTable } from 'views/main/organization/analytics-v3/hooks/useAnalyticsTable';
import { useGetAnalyticsNoiseReductionByService } from 'views/main/organization/analytics-v3/hooks/useGetAnalyticsData';
import { useNoiseReductionTabContext } from 'views/main/organization/analytics-v3/hooks/useNoiseReductionTabContext';
import { NewTabIcon } from 'views/main/organization/analytics-v3/icons/new-tab';
import { SERVICE_DETAILS_PATH } from 'views/main/routes/routes';
import { useShallow } from 'zustand/react/shallow';
import Donut from '../../../Charts/Donut';
import AnalyticsTable from '../../../Table/AnalyticsTable';
import { ANALYTIC_V3_EVENTS } from 'views/main/organization/analytics-v3/constants';
import { AppTracker } from 'shared/analytics/tracker';
import Empty from 'views/main/organization/analytics-v3/icons/empty';
import Error from 'views/main/organization/analytics-v3/components/Error';
import { AxiosError } from 'axios';
export const NoiseReductionByServiceChart = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const {
    data: NoiseReductionData,
    isLoading: isLoadingNoiseReduction,
    isRefetching,
    isError,
    refetch: refetchNoiseReductionByService,
    error,
  } = useGetAnalyticsNoiseReductionByService();

  const selectedNoiseReductionData = useNoiseReductionTabContext(
    useShallow(state => state.selectedNoiseReductionData),
  );
  const noiseReductionTablepaginationInfo = useNoiseReductionTabContext(
    useShallow(state => state.noiseReductionTablepaginationInfo),
  );
  const setSelectedNoiseReductionData = useNoiseReductionTabContext(
    useShallow(state => state.setSelectedNoiseReductionData),
  );

  const onServiceSelectinNoiseReduction = (serviceID: string) => {
    const idToSearch = serviceID;

    const filtered = NoiseReductionData?.data?.metrics?.find(
      (ele: any) => ele.serviceID === idToSearch, // Compare serviceId as number
    );

    if (filtered) {
      setSelectedNoiseReductionData(filtered);
    } else {
      console.warn(`Service with ID ${serviceID} not found.`);
    }
  };

  useEffect(() => {
    if (
      NoiseReductionData?.data?.metrics?.length &&
      NoiseReductionData?.data?.metrics?.length > 0
    ) {
      onServiceSelectinNoiseReduction(NoiseReductionData?.data?.metrics[0]?.serviceID);
    }
  }, [isLoadingNoiseReduction, NoiseReductionData]);

  const noiseReductionChartData = selectedNoiseReductionData
    ? generateDonutDataForNoiseReduction(selectedNoiseReductionData)
    : [];

  const { NoiseReductionTableColumns } = useAnalyticsTable();

  const setNoiseReductionPagination = useNoiseReductionTabContext(
    useShallow(state => state.setNoiseReductionPagination),
  );

  const handlePaginationChange = (page: Partial<PaginationType>) => {
    setNoiseReductionPagination(page);
  };

  const handleSortChange = (sort: Array<SortBy>) => {
    const { id, desc } = sort[0];
    AppTracker.track(
      ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_SORTED,
      {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Alert Noise Reduction',
        'Chart Title': 'Noise Reduction by Service',
        'Sort By': id,
        'Sort Order': desc ? 'Descending' : 'Ascending',
      },
      true,
    );
    setNoiseReductionPagination({
      sortBy: sort,
    });
  };

  const handleServiceClick = (serviceID?: string) => {
    if (!serviceID) return;
    // generate path for service
    const generatedPath = generatePath(SERVICE_DETAILS_PATH, {
      id: serviceID,
    });
    // open in a new tab
    window.open(generatedPath, '_blank');
  };
  if (isError) {
    return (
      <Box position="relative" height={380}>
        <Error
          onRefreshClick={refetchNoiseReductionByService}
          errorMessage={(error as AxiosError).response?.data?.message}
        />
      </Box>
    );
  }

  if (!NoiseReductionData) return null;
  const isEmpty = NoiseReductionData?.data?.metrics?.length === 0;
  if (isEmpty)
    return (
      <Box position="relative" height={380}>
        <Empty />
      </Box>
    );

  return (
    <Grid flexWidth={12}>
      <Grid flexWidth={6}>
        <AnalyticsTable
          columns={NoiseReductionTableColumns}
          selectors={{
            id: 'selecedService',
            header: '',
            size: 10,
            cell: ({ row }) => (
              <Radio
                key={row.original.serviceID}
                value={row.original.serviceID}
                isChecked={row.original.serviceID === selectedNoiseReductionData?.serviceID}
                onClick={() => onServiceSelectinNoiseReduction(row.original.serviceID)}
              />
            ),
          }}
          data={NoiseReductionData?.data?.metrics}
          pageCount={NoiseReductionData?.metadata?.totalCount || 0}
          pageIndex={noiseReductionTablepaginationInfo?.pageIndex || 0}
          pageSize={noiseReductionTablepaginationInfo?.pageSize || 0}
          onPaginationChange={handlePaginationChange}
          key={selectedNoiseReductionData?.serviceID || 'defaultKey'} // Change key when selected serviceId changes
          onSortChange={handleSortChange}
          isLoading={isLoadingNoiseReduction || isRefetching}
          sorting={noiseReductionTablepaginationInfo.sortBy}
        />
      </Grid>

      <Grid flexWidth={6} style={{ position: 'relative', height: 380 }}>
        <Box position="absolute" top={0} left={8} width="100%" height="100%" zIndex={1}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => handleServiceClick(selectedNoiseReductionData?.serviceID)}
          >
            <Ellipsis text={selectedNoiseReductionData?.serviceName ?? ''} maxLength={25} />
            <Box ml={2}>
              <NewTabIcon />
            </Box>
          </Button>
        </Box>
        <Donut
          chartName="Noise Reduction by Service"
          data={noiseReductionChartData}
          showLabel={true}
          showLabelLine={true}
          showLegend={false}
          height={380}
          width={'100%'}
          center={['50%', '50%']}
          radius={['40%', '60%']}
        />
      </Grid>
    </Grid>
  );
};
